import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import DataTransferService from '@/services/dataTransferService'
import { useUserStore } from '@/stores/userStore'
import useLogger from '@/services/loggerService'

import useBlProviders from '@/composables/providers/useBlProvider'

const dataTransferService = DataTransferService()

export default function useNotifications() {
	const { getPreparedQueryForDt } = useBlProviders()

	const userStore = useUserStore()
	const logger = useLogger()
	const { defaultBlFlags } = useBlProviders()

	const { t: $t } = useI18n()

	const notifications = ref([])
	const notification = ref({})
	const notificationsUnreadCount = ref({})
	const notificationPreview = ref({})

	const flags = reactive({
		...defaultBlFlags,
	})

	const defaults = {
		creatingNotifications: {
			id: 0,
			created_at: '',
			is_read: true,
			content: '',
			notification_type: '',
			map_info: {},
			action_info: {},
			on_settings: null,
			// rule: {
			//   id: 0,
			//   config: {},
			//   is_active: true,
			//   user: 0,
			//   company: 0,
			//   channels: [],
			// },
			// title: '',
			// annotation: '',
			// category: '',
			// subcategory: '',
			// importance_level: '',
		},
		fieldsToPatch: ['created_at', 'is_read', 'content', 'notification_type', 'map_info', 'action_info', 'on_settings'],
	}

	const getNotifications = (payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to', 'count')
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			dataTransferService.notifications
				.getNotifications(userStore.selectedCompanyId, preparedQuery)
				.then((res) => {
					notifications.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notifications'),
					})

					reject()
				})
		})
	}

	const getNotificationsUnreadCount = (payload, serverVersion) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to', 'count')
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			dataTransferService.notifications
				.getNotificationsUnreadCount(userStore.selectedCompanyId, preparedQuery, serverVersion)
				.then((res) => {
					notificationsUnreadCount.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notifications'),
					})

					reject()
				})
		})
	}

	const getNotification = (notificationId, payload) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to', 'count')
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			dataTransferService.notifications
				.getNotification(userStore.selectedCompanyId, notificationId, preparedQuery)
				.then((res) => {
					notification.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notification'),
					})

					reject()
				})
		})
	}

	const getNotificationPreview = (notificationId) => {
		return new Promise((resolve, reject) => {
			flags.isLoadingPreview = true

			dataTransferService.notifications
				.getNotificationPreview(userStore.selectedCompanyId, notificationId)
				.then((res) => {
					notificationPreview.value = res
					flags.isLoadingPreview = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoadingPreview = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notification'),
					})

					reject()
				})
		})
	}

	const updateNotifications = (notificationId, body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			dataTransferService.notifications
				.patchNotifications(userStore.selectedCompanyId, notificationId, body)
				.then((res) => {
					flags.isUpdating = false

					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error updating notification'),
					})

					reject()
				})
		})
	}

	const updateNotificationsMarkAllRead = (body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			dataTransferService.notifications
				.patchNotificationsMarkAllRead(userStore.selectedCompanyId, body)
				.then((res) => {
					flags.isUpdating = false

					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error updating notification mark all read'),
					})

					reject()
				})
		})
	}

	const updateNotificationsMarkReadStatus = (body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			dataTransferService.notifications
				.patchNotificationsMarkReadStatus(userStore.selectedCompanyId, body)
				.then((res) => {
					flags.isUpdating = false

					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Loading error'),
					})

					reject()
				})
		})
	}

	const getNotificationsSettings = () => {
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			dataTransferService.notifications
				.getNotificationsSettings(userStore.selectedCompanyId)
				.then((res) => {
					resolve(res)
				})
				.catch(() => {
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notifications settings'),
					})

					reject()
				})
				.finally(() => {
					flags.isLoading = false
				})
		})
	}

	const updateNotificationsSettings = (notificationSettingId, body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			dataTransferService.notifications
				.patchNotificationsSettings(userStore.selectedCompanyId, notificationSettingId, body)
				.then((res) => {
					resolve(res)
				})
				.catch(() => {
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error updating notifications settings'),
					})

					reject()
				})
				.finally(() => {
					flags.isUpdating = false
				})
		})
	}

	return {
		flags,
		defaults,
		notifications,
		notification,
		notificationsUnreadCount,
		notificationPreview,

		getNotifications,
		getNotificationsUnreadCount,
		getNotification,
		getNotificationPreview,
		updateNotifications,
		updateNotificationsMarkAllRead,
		updateNotificationsMarkReadStatus,
		getNotificationsSettings,
		updateNotificationsSettings,
	}
}
